import * as React from 'react';

import { Dispatch } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import { formatMoney } from '@webapp/utils/formatMoney';

import { CloseMenu, OpenMenu } from '@webapp/store/ui/action-creators';

import { SubscriptionType } from '@common/types/subscription-manager/list-subscriptions-response';

import Menu from '@components/menu';
import Flex from '@components/layout/flex';
import PlusIcon from '@components/basics/icons/plus';
import Typography from '@components/basics/typography';
import CloseIcon from '@components/basics/icons/close';
import DotsCircleIcon from '@components/basics/icons/dots-circle';
import { CallHistoryMethodAction, push } from 'connected-react-router';
import AppState from '@webapp/store/state';
import { CreateOrder } from '@webapp/store/orders/action-creators';
import { getTracProPlan } from '../../../store/products/selectors';

function getPromoText(value: SubscriptionType) {
  switch (value) {
    case 'trac-pro':
      return <Translate id="ARTIST_PROFILE.SUBSCRIPTION.PRO.TEXT" />;
    case 'distribution':
      return <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.TEXT" />;
    case 'merch':
      return <Translate id="ARTIST_PROFILE.SUBSCRIPTION.MERCH.TEXT" />;
    case 'creative':
      return <Translate id="ARTIST_PROFILE.SUBSCRIPTION.CREATIVE.TEXT" />;
    case 'experiences':
      return <Translate id="ARTIST_PROFILE.SUBSCRIPTION.EXPERIENCES.TEXT" />;

    default:
      return <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.TEXT" />;
  }
}

function getSubscriptionTitle(value: SubscriptionType) {
  switch (value) {
    case 'trac-pro':
      return 'trac';
    case 'distribution':
      return <Translate id="ARTIST_PROFILE.SUBSCRIPTION.DISTRIBUTION.TITLE" />;
    case 'merch':
      return <Translate id="ARTIST_PROFILE.SUBSCRIPTION.MERCH.TITLE" />;
    case 'creative':
      return <Translate id="ARTIST_PROFILE.SUBSCRIPTION.CREATIVE.TITLE" />;
    case 'experiences':
      return <Translate id="ARTIST_PROFILE.SUBSCRIPTION.EXPERIENCES.TITLE" />;

    default:
      return 'trac';
  }
}

interface SubscriptionItemProps {
  className?: string;
  isActive: boolean;
  id: string;
  artistId: string;
  type: SubscriptionType;
  plan?: string;
  price?: number;
  title: React.ReactNode;
  text: React.ReactNode;
  frequency?: string;
  isExpiring?: boolean;
  closeMenu: () => CloseMenu;
  goToPlans: (clientId: string, id: string) => CallHistoryMethodAction;
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  closeMenu: () => dispatch(new CloseMenu()),
  goToPlans: (clientId: string, id: string) => {
    return dispatch(push(`/payment/${clientId}/${id || ''}`));
  },
});

const SubscriptionItem: React.FC<SubscriptionItemProps> = (props) => {
  const dispatch = useDispatch();

  const tracPro = useSelector(getTracProPlan);

  const url = useSelector((state: AppState) => state.router.location.pathname);
  const hash = useSelector((state: AppState) => state.router.location.hash);

  const goToHandlePlans = () => {
    if (!tracPro) {
      return;
    }
    dispatch(
      new CreateOrder({
        product: tracPro,
        price: tracPro.prices.find((price) => price.frequency === 'year')!,
        quantity: 1,
      })
    );
    props.goToPlans(props.artistId, props.id);
  };

  const goToCancelPro = () => {
    dispatch(push('/unsubscribe'));
  };

  React.useEffect(() => {
    if (url === '/artist/subscriptions' && hash === '#openDistMenu') {
      dispatch(new OpenMenu('DISTRIBUTION_TRIAL'));
    }
  }, [hash, dispatch, url]);

  return (
    <div>
      <StyledBox
        className={`${props.className || ''} ${
          props.isActive ? 'active' : ''
        } ${props.type === 'merch' && props.isExpiring ? 'has-hint' : ''}`}
      >
        <Flex alignItems="center" justifyContent="space-between" gap={20}>
          <Flex alignItems="center">
            <div>
              <Flex alignItems="center">
                <Typography className="title" weight="700">
                  {getSubscriptionTitle(props.type)}
                </Typography>
                {props.type === 'distribution' ||
                props.type === 'merch' ||
                props.type === 'trac-pro' ? (
                  <Badge>
                    <Typography weight="700" type="small">
                      Pro
                    </Typography>
                  </Badge>
                ) : null}
              </Flex>
              <Typography
                className="subscription-text"
                type="small"
                opacity="1"
              >
                {props.isActive ? (
                  <>
                    <Translate
                      id={
                        props.frequency === 'year'
                          ? 'ARTIST_PROFILE.SUBSCRIPTION.YEAR'
                          : 'ARTIST_PROFILE.SUBSCRIPTION.MONTH'
                      }
                    />{' '}
                    • {props.text}
                  </>
                ) : props.type ? (
                  <Typography
                    className="subscription-text"
                    type="small"
                    opacity="1"
                  >
                    {getPromoText(props.type)}
                  </Typography>
                ) : null}
              </Typography>
            </div>
          </Flex>
          <Flex alignItems="flex-end" justifyContent="center" gap={10}>
            {props.isActive && !props.isExpiring ? (
              <>
                {props.price ? (
                  <MoneyTag>
                    <Typography weight="700">
                      {formatMoney(props.price)}
                    </Typography>
                  </MoneyTag>
                ) : null}
                <Menu
                  className="menu"
                  button={
                    <DotsCircleIcon className="open-icon" isInvertedColor />
                  }
                  closeButton={
                    <CloseIcon
                      onClick={props.closeMenu}
                      className="close-icon"
                      isInvertedColor
                    />
                  }
                  customIconHeight="32px"
                  menuId={`SUBSCRIPTION-ITEM-MENU-${props.id}`}
                >
                  <MenuContent>
                    {props.type === 'trac-pro' ? (
                      <Typography
                        weight="700"
                        className="menu-item danger"
                        onClick={() => goToCancelPro()}
                      >
                        <Translate id="ARTIST_PROFILE.SUBSCRIPTION.MENU.CANCEL" />
                      </Typography>
                    ) : null}
                    {props.plan?.includes('Pro') &&
                    props.type === 'distribution' ? (
                      <Typography
                        weight="700"
                        className="menu-item danger"
                        onClick={() => goToHandlePlans()}
                      >
                        <Translate id="ARTIST_PROFILE.SUBSCRIPTION.MENU.DOWNGRADE" />
                      </Typography>
                    ) : null}
                  </MenuContent>
                </Menu>
              </>
            ) : (
              <PlusButton>
                <PlusIcon isWhite onClick={() => goToHandlePlans()} />
              </PlusButton>
            )}
          </Flex>
        </Flex>
      </StyledBox>
    </div>
  );
};

export default connect(
  null,
  mapDispatchToProps
)(styled(SubscriptionItem)`
  .menu {
    .open-icon,
    .close-icon {
      width: 32px;
      height: 32px;
      cursor: pointer;
    }
  }
`);

const StyledBox = styled.div`
  padding: 20px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.differentBackground};
  border: 1px dashed ${({ theme }) => theme.colors.border};

  &.active {
    background-color: ${({ theme }) => theme.colors.primary};
    border: 1px solid transparent;

    .title {
      color: ${({ theme }) => theme.colors.background};
    }
  }

  .subscription-text {
    color: ${({ theme }) => theme.colors.subscriptionBoxText};
    margin-top: 5px;
  }

  &.has-hint {
    border-radius: 4px 4px 0 0;
  }
`;

const Badge = styled.div`
  background: ${({ theme }) => theme.colors.blueColor};
  padding: 2px 8px;
  border-radius: 90px;
  margin-left: 5px;

  span {
    color: ${({ theme }) => theme.colors.white};
  }
`;

const MoneyTag = styled.div`
  background-color: ${({ theme }) => theme.colors.moneyChip};
  padding: 8px 10px;
  border-radius: 90px;
  span {
    color: ${({ theme }) => theme.colors.background};
  }
`;

const MenuContent = styled.div`
  display: grid;
  grid-gap: 5px;
  min-width: 128px;

  .menu-item {
    cursor: pointer;
    transition: color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};

    &:hover {
      color: ${({ theme }) => theme.colors.tertiary};
    }

    &.danger {
      color: ${({ theme }) => theme.colors.accent};
      &:hover {
        color: ${({ theme }) => theme.colors.accentHover};
      }
    }
  }
`;

const PlusButton = styled.div`
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.blueColor};
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  svg {
    padding: 8px;
    width: 32px;
    height: 32px;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.blueColorDark};
  }
`;
